"use client";
import Icon from "../../../components/AppIcon";
import Image from "../../../components/AppImage";
import React, { useState, useEffect } from "react";
import FilterSystem from "./FilterSystem.jsx";
import CaseStudyCard from "./CaseStudyCard.jsx";
import CaseStudyViewer from "./CaseStudyViewer.jsx";
import RelatedWork from "./RelatedWork.jsx";
import { Projects } from "data/projects";
import CountUp from "react-countup";

const PortfolioInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedIndustries, setSelectedIndustries] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedOutcomes, setSelectedOutcomes] = useState([]);
  const [selectedCaseStudy, setSelectedCaseStudy] = useState(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  // Dynamically generate filters from Projects data
  const industries = Object.entries(
    Projects.reduce((acc, project) => {
      const key = project.industry.toLowerCase();
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {}),
  ).map(([id, count]) => ({
    id,
    label: id
      .split(" ")
      .map((word) => word[0].toUpperCase() + word.slice(1))
      .join(" "),
    count,
  }));

  const services = Object.entries(
    Projects.reduce((acc, project) => {
      project.services.forEach((s) => {
        const key = s.toLowerCase().replace(/\s+/g, "-");
        acc[key] = (acc[key] || 0) + 1;
      });
      return acc;
    }, {}),
  ).map(([id, count]) => ({
    id,
    label: id.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
    count,
  }));

  const outcomes = Object.entries(
    Projects.reduce((acc, project) => {
      project.outcomes.forEach((o) => {
        acc[o] = (acc[o] || 0) + 1;
      });
      return acc;
    }, {}),
  ).map(([id, count]) => ({
    id,
    label: id.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
    count,
  }));

  const handleIndustryToggle = (id) => {
    if (!isHydrated) return;
    setSelectedIndustries((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  const handleServiceToggle = (id) => {
    if (!isHydrated) return;
    setSelectedServices((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id],
    );
  };

  const handleOutcomeToggle = (id) => {
    if (!isHydrated) return;
    setSelectedOutcomes((prev) =>
      prev.includes(id) ? prev.filter((o) => o !== id) : [...prev, id],
    );
  };

  const handleClearAll = () => {
    if (!isHydrated) return;
    setSelectedIndustries([]);
    setSelectedServices([]);
    setSelectedOutcomes([]);
  };

  const handleViewDetails = (id) => {
    if (!isHydrated) return;
    setSelectedCaseStudy(id);
  };

  const handleCloseViewer = () => {
    if (!isHydrated) return;
    setSelectedCaseStudy(null);
  };

  const handleDownloadPDF = (id) => {
    if (!isHydrated) return;
    console.log(`Downloading PDF for case study: ${id}`);
    alert("PDF download functionality would be implemented here");
  };

  const handleShare = (id, platform) => {
    if (!isHydrated) return;
    console.log(`Sharing case study ${id} on ${platform}`);
    alert(`Sharing on ${platform} functionality would be implemented here`);
  };

  const filteredProjects = isHydrated
    ? Projects.filter((cs) => {
        const industryMatch =
          selectedIndustries.length === 0 ||
          selectedIndustries.some((ind) =>
            cs.industry.toLowerCase().includes(ind),
          );

        const serviceMatch =
          selectedServices.length === 0 ||
          selectedServices.some((serv) =>
            cs.services.some((s) =>
              s.toLowerCase().replace(/\s+/g, "-").includes(serv),
            ),
          );

        const outcomeMatch =
          selectedOutcomes.length === 0 ||
          selectedOutcomes.some((out) => cs.outcomes.includes(out));

        return industryMatch && serviceMatch && outcomeMatch;
      })
    : Projects;

  const currentCaseStudy =
    isHydrated && selectedCaseStudy
      ? Projects.find((cs) => cs.id === selectedCaseStudy)
      : null;

  const relatedCases =
    isHydrated && currentCaseStudy
      ? Projects.filter(
          (cs) =>
            cs.id !== currentCaseStudy.id &&
            (cs.industry === currentCaseStudy.industry ||
              cs.services.some((s) => currentCaseStudy.services.includes(s))),
        )
          .slice(0, 3)
          .map((cs) => ({
            id: cs.id,
            title: cs.title,
            client: cs.client,
            image: cs.image,
            alt: cs.alt,
            services: cs.services,
          }))
      : [];

  if (!isHydrated) {
    return (
      <section
      data-theme="light" className="py-4">
        <div className="container mx-auto px-4">
          <div className="animate-pulse space-y-8">
            <div className="h-64 bg-muted rounded-lg"></div>
            <div className="grid md:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="h-96 bg-muted rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      data-theme="light">
      <div className="py-4">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-5xl mx-auto mt-18">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-accent/10 backdrop-blur-md text-xs sm:text-sm border border-accent/30 rounded-full px-5 py-1.5 mb-2 sm:mb-4">
              <span className="text-accent font-medium tracking-wide">
                Portfolio Excellence
              </span>
            </div>

            {/* Heading */}
            <h1 className="text-4xl sm:text-6xl  font-bold text-neutral-900 mb-2 sm:mb-6 leading-[1.05]">
              Where Vision Meets{" "}
              <span className=" mt-2 bg-clip-text text-red-600">
                Precision
              </span>
            </h1>

            {/* Subtitle */}
            <p className="max-w-2xl mx-auto text-base sm:text-lg md:text-xl text-neutral-600 mb-2 sm:mb-4 leading-relaxed">
              Explore a portfolio of
              <span className="font-medium text-red-600">
                {" "}
                cinematic storytelling{" "}
              </span>
              that transforms brands through
              <span className="font-medium text-red-600">
                {" "}
                technical mastery{" "}
              </span>
              and
              <span className="font-medium text-red-600"> creative vision</span>
              .
            </p>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-10 mb-2 sm:mb-4">
              {[
                { end: 5, suffix: "+", label: "Projects Completed" },
                { end: 50, suffix: "+", label: "Fortune 500 Clients" },
                { static: "25+", label: "Industry Awards" },
                { end: 3, suffix: "+", label: "Years Excellence" },
              ].map((item, i) => (
                <div
                  key={i}
                  className="rounded-2xl bg-white/40 backdrop-blur-sm border border-black/5 py-2 px-2 shadow-subtle"
                >
                  <div className="text-3xl md:text-4xl font-bold text-accent mb-1">
                    {item.static ?? (
                      <CountUp
                        end={item.end}
                        duration={2}
                        suffix={item.suffix}
                        enableScrollSpy
                        scrollSpyOnce
                      />
                    )}
                  </div>
                  <div className="text-neutral-600 text-xs uppercase tracking-widest">
                    {item.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4">
          <FilterSystem
            industries={industries}
            services={services}
            outcomes={outcomes}
            selectedIndustries={selectedIndustries}
            selectedServices={selectedServices}
            selectedOutcomes={selectedOutcomes}
            onIndustryToggle={handleIndustryToggle}
            onServiceToggle={handleServiceToggle}
            onOutcomeToggle={handleOutcomeToggle}
            onClearAll={handleClearAll}
            className="mb-4"
          />

          <div className="mb-4">
            <p className="text-lg text-muted-foreground">
              Showing{" "}
              <span className="font-bold text-foreground">
                {filteredProjects.length}
              </span>{" "}
              case studies
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {filteredProjects.map((cs) => (
              <CaseStudyCard
                key={cs.id}
                caseStudy={cs}
                onViewDetails={handleViewDetails}
              />
            ))}
          </div>

          {filteredProjects.length === 0 && (
            <div className="text-center py-4">
              <p className="text-xl text-muted-foreground mb-4">
                No case studies match your selected filters
              </p>
              <button
                onClick={handleClearAll}
                className="px-6 py-3 bg-primary text-primary-foreground font-bold rounded-lg"
              >
                Clear All Filters
              </button>
            </div>
          )}
        </div>
      </div>

      {currentCaseStudy && (
        <>
          <CaseStudyViewer
            caseStudy={currentCaseStudy}
            onClose={handleCloseViewer}
            onDownloadPDF={handleDownloadPDF}
            onShare={handleShare}
          />
          {relatedCases.length > 0 && (
            <RelatedWork cases={relatedCases} onViewCase={handleViewDetails} />
          )}
        </>
      )}
    </section>
  );
};

export default PortfolioInteractive;
